import type { ReelMotionTuning } from "./ReelMotionTuning";
import type { ComputedGridLayout, SpeedMode, SymbolView } from "@slot/presentation";

/** The slam's synchronized brake, in logical seconds. */
export const SLAM_DECEL_SECONDS = 0.16;

/**
 * Buffer drum lanes ABOVE the visible window (one rotation = gridRowCount + this).
 * Shared by ReelDrum (drum geometry) and ReelColumn (at-rest slot map).
 */
export const BUFFER_ROWS_ABOVE = 2;

// AXIS NOTE (Pixi port): Pixi's y grows DOWN. The drum scroll offset `off` is still "pixels
// scrolled downward" (sign-agnostic maths); only where an offset becomes a
// lane y does the sign matter: y = baseY + off, the band edge is BELOW the
// last row (bandBottom = originY + rows·spacing), lanes wrap by SUBTRACTING the drum height, and
// a lane is hidden once y >= bandBottom.

/** Cubic ease-out (1 - (1-t)^3). The slam snap + the settle spring-back. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Quadratic ease-out (1 - (1-t)^2). The natural brake + the DEFAULT wind-up pull-up. */
export function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/** Quadratic ease-in (t^2). A wind-up easing option. */
function easeInQuad(t: number): number {
  return t * t;
}

/** Quadratic ease-in-out (soft start, accelerate, soft finish). A wind-up easing option. */
function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Wind-up pull-up easing options, by tuning name (`ReelMotionTuning.spin.windUpEasing`).
 * Pure t∈[0,1]→[0,1] curves for the analytic WINDUP offset (the drum is not tweened, so
 * these are not the runner's `TweenEasing`s).
 * Deliberately the quad family + linear — the wind-up is a small pull, not a place for
 * overshoot/elastic curves (which would dip past the rest line or the pull peak).
 */
const WIND_UP_EASINGS: Record<string, (t: number) => number> = {
  linear: (t) => t,
  quadIn: easeInQuad,
  quadOut: easeOutQuad,
  quadInOut: easeInOutQuad,
};

/**
 * Resolve a wind-up easing NAME to its curve. Unknown / omitted → `easeOutQuad` (the
 * historical default), so a reel game that doesn't set `windUpEasing` is unchanged.
 * Resolved ONCE per spin in `computeSpinParams` (stored on `SpinParams`).
 */
export function resolveWindUpEasing(name?: string): (t: number) => number {
  return (name && WIND_UP_EASINGS[name]) || easeOutQuad;
}

/** One drum lane: a pooled SymbolView + its rest-Y + last-applied-Y. */
export interface ReelLane {
  view: SymbolView;
  baseY: number;
  prevY: number;
}

/**
 * Immutable per-spin config — geometry (from layout/rowcount) + the speed-mode-
 * derived motion params. Computed ONCE per spin by computeSpinParams (below) and
 * read by every ReelColumn via its context.
 */
export interface SpinParams {
  // geometry
  spacing: number;
  laneCount: number;
  drumHeight: number;
  bandBottom: number;
  originY: number;
  gridRowCount: number;
  // motion
  v: number; // logical px/sec scroll speed
  overshoot: number;
  settleSeconds: number;
  windUpPixels: number;
  windUpSeconds: number;
  windUpEase: (t: number) => number; // resolved wind-up pull-up curve (see resolveWindUpEasing)
  spinUpSeconds: number;
  startStaggerSeconds: number;
  stopStaggerSeconds: number;
  baseMinSpinSeconds: number;
  brakeDistance: number;
  slamMinTravel: number;
  slamDecelSeconds: number;
  // anticipation (0 duration ⇒ feature off)
  antV: number; // logical px/sec slow-scroll speed (v * slowVelocityMultiplier)
  anticipationDurationSeconds: number; // total beat (ramp + slow hold); 0 = disabled
  anticipationRampInSeconds: number; // ramp v→antV at entry; 0 = hard drop
  anticipationCrisp: boolean; // render the anticipating reel's fillers crisp (no blur)
  anticipationStartDelaySeconds: number; // pause before the FIRST anticipated reel's beat (from prev land)
}

/**
 * What a ReelColumn needs from the drum, bound once per spin via beginSpin().
 * `finals()` and `aborted()` are LIVE getters — the server response (finals) and the
 * abort signal both arrive MID-spin (after the drum is already scrolling), so they
 * must be read each frame, never snapshotted. `applyLaneSymbol`/`pickSymbolId`/
 * `acquire`/`release` delegate to the drum's pool + symbol binding.
 */
export interface ReelColumnContext {
  params: SpinParams;
  applyLaneSymbol(view: SymbolView, symbolId: string, isFiller: boolean): void;
  pickSymbolId(): string;
  acquire(): SymbolView;
  release(view: SymbolView): void;
  finals(): string[][] | null;
  aborted(): boolean;
  /**
   * LIVE getter for the server-flagged anticipation columns (0-based), or null. Arrives
   * MID-spin with the finals, so it's read at the SCROLL gate, never snapshotted.
   */
  anticipates(): number[] | null;
  /** Fired by the column the frame it lands (its impact, before any settle bounce), in the driver's stop order. */
  onColumnLanded?(col: number): void;
  /** Fired by a flagged column the frame it ENTERS the anticipation beat (frame + sound). */
  onColumnAnticipateStart?(col: number): void;
}

/**
 * The cross-column land-stagger cursor — the ONLY shared mutable state the per-column
 * tick touches. The driver creates ONE per spin and passes it to every column.tick():
 * reels commit their landing strictly left→right (nextNaturalCol) and each lands
 * ≥ stopStagger after the previous (naturalLandCursor). It lives at the driver, not on a
 * column, because it sequences ACROSS columns.
 */
export interface ReelLandStaggerCursor {
  nextNaturalCol: number;
  naturalLandCursor: number;
  /**
   * Highest column that has reached its landing (impact — the frame onColumnLanded fires); -1
   * before any reel lands. A flagged reel gates its ANTICIPATE entry on `lastLandedCol >= col - 1`
   * so the tease begins only AFTER the previous reel has actually LANDED (not merely committed).
   */
  lastLandedCol: number;
  /** Logical time the most-recent reel landed — the FIRST anticipated reel waits startDelay past this. */
  lastLandedTime: number;
  /** True once any reel has entered its beat this spin — only the FIRST anticipated reel gets startDelay. */
  anticipationStarted: boolean;
}

/**
 * Compute the immutable per-spin config (geometry + speed-mode-derived motion
 * params) once per spin. Read by every `ReelColumn` via `params.*`. Staggers
 * + wind-up + spin-up are a `normal`-mode feel only (turbo/super_turbo run
 * lockstep); every duration/speed is in LOGICAL (normal-mode) seconds — the runner
 * clock-scales the tick, so turbo/super come out proportionally faster for free,
 * with NO explicit time-scale math.
 */
export function computeSpinParams(layout: ComputedGridLayout, gridRowCount: number, tuning: ReelMotionTuning, mode: SpeedMode): SpinParams {
  const spacing = layout.cellHeight;
  const laneCount = gridRowCount + BUFFER_ROWS_ABOVE;
  const drumHeight = laneCount * spacing;
  const bandBottom = layout.originY + gridRowCount * spacing; // below the last row (Pixi y-down)

  const spin = tuning.spin;
  const stop = tuning.stop;
  const slamDecelSeconds = SLAM_DECEL_SECONDS;

  const staggered = mode === "normal";
  const startStaggerSeconds = staggered ? spin.reelStartStaggerSeconds : 0;
  const stopStaggerSeconds = staggered ? stop.reelStopStaggerSeconds : 0;
  const baseMinSpinSeconds = staggered
    ? spin.minSpinDurationSeconds
    : spin.turboMinSpinDurationSeconds ?? spin.minSpinDurationSeconds;
  const windUpPixels = staggered
    ? Math.min(spin.windUpPixels ?? 0, spacing * 0.45)
    : 0;
  const windUpSeconds = Math.max(0.01, spin.windUpSeconds ?? 0.12);
  // Wind-up pull-up curve, resolved by name once per spin (omit → easeOutQuad).
  const windUpEase = resolveWindUpEasing(spin.windUpEasing);
  // Spin-up: ease-in (constant accel) from rest to full scroll speed after the
  // wind-up, so the release out of the pull-up is smooth instead of a 0→v jump.
  // Normal-mode only (turbo/super snap to full speed). 0 → no ramp (scroll
  // starts at full v — the prior behavior).
  const spinUpSeconds = staggered ? Math.max(0, spin.spinUpDurationSeconds) : 0;

  const v = spin.scrollSpeedRowsPerSecond * spacing; // logical px/sec
  // Min forward travel a SLAM lands over, so an at-rest/held reel still scrolls
  // a visible amount (never pops). The floor is applied in whole drumHeights
  // (commitSlam), so the landing stays on a full-rotation boundary.
  const slamMinTravel = drumHeight * 0.5;
  // Natural-stop brake distance (px): the reel cruises, then brakes over the
  // last `brakeDistance` px onto its (row-aligned) landing — the ONLY natural-
  // brake knob. Duration is velocity-matched (2·dist/v) so the brake enters at
  // the scroll speed (no lurch). ~1+ cells reads cleanest; tiny values pop.
  const brakeDistance = Math.max(0, stop.brakeDistancePixels);

  // Landing overshoot: the decel carries the reel PAST the rest line to an
  // overshoot low-point (continuous with the incoming scroll — it never stops
  // ON the line), then a single SETTLE phase springs it back UP to rest.
  // Clamped under half a cell so the small dip never trips applyOff's
  // wrap-relabel. 0 → land straight on the boundary (no overshoot, no settle).
  const overshoot =
    stop.bounce.enable && stop.bounce.pixels > 0 && stop.bounce.durationSeconds > 0
      ? Math.min(stop.bounce.pixels, spacing * 0.45)
      : 0;
  const settleSeconds = Math.max(0.01, stop.bounce.durationSeconds);

  // Anticipation (server-flagged slow + extended beat). Optional tuning → 0 duration
  // disables it (the gate never enters ANTICIPATE). Velocities are logical; the runner
  // clock-scales them like everything else, and turbo can shorten the beat further.
  const ant = tuning.anticipation;
  const antV = ant ? v * Math.max(0, ant.slowVelocityMultiplier) : 0;
  const anticipationDurationSeconds = ant
    ? staggered
      ? Math.max(0, ant.durationSeconds)
      : Math.max(0, ant.turboDurationSeconds ?? ant.durationSeconds)
    : 0;
  const anticipationRampInSeconds = ant
    ? Math.min(Math.max(0, ant.rampInSeconds), anticipationDurationSeconds)
    : 0;
  const anticipationCrisp = ant ? !!ant.crispFillers : false;
  const anticipationStartDelaySeconds = ant ? Math.max(0, ant.startDelaySeconds) : 0;

  return {
    spacing,
    laneCount,
    drumHeight,
    bandBottom,
    originY: layout.originY,
    gridRowCount,
    v,
    overshoot,
    settleSeconds,
    windUpPixels,
    windUpSeconds,
    windUpEase,
    spinUpSeconds,
    startStaggerSeconds,
    stopStaggerSeconds,
    baseMinSpinSeconds,
    brakeDistance,
    slamMinTravel,
    slamDecelSeconds,
    antV,
    anticipationDurationSeconds,
    anticipationRampInSeconds,
    anticipationCrisp,
    anticipationStartDelaySeconds,
  };
}
